/**
 * The prize search field + Filters button (Figma 9169:57586), shared verbatim
 * between the learner shop (/learn/prizes) and Prize Management — product rule
 * 2026-07-20: both surfaces show the EXACT same filter UI. The admin surface
 * additionally passes `categories`, which renders a Category facet inside the
 * same panel.
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { CheckIcon, StarIcon } from '@heroicons/react/24/solid';
import { EASE_OUT } from '@/lib/motion';

export type PrizeGender = 'men' | 'women' | 'unisex';

interface SearchFieldProps {
  value: string;
  onChange: (next: string) => void;
}

export function PrizeSearchField({ value, onChange }: SearchFieldProps) {
  return (
    <label className="relative flex items-center gap-2 w-full sm:max-w-[260px] h-11 px-3 rounded-lg border border-text-primary/60 dark:border-border-primary/60 bg-surface-elevated focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20 transition-[border-color,box-shadow] duration-fast ease-out-strong">
      <MagnifyingGlassIcon className="w-5 h-5 text-text-primary/60" aria-hidden="true" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="key words.."
        className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm text-text-primary placeholder:text-text-primary/60"
      />
    </label>
  );
}

interface PrizeFiltersButtonProps {
  /** Multi-select: an empty array means "no filter". */
  gender: PrizeGender[];
  onGender: (next: PrizeGender[]) => void;
  color: string[];
  onColor: (next: string[]) => void;
  /** Distinct color names present across the loaded prizes (lowercase). */
  colors: string[];
  scoreRange: [number, number] | null;
  onScoreRange: (next: [number, number] | null) => void;
  maxScore: number;
  /** Admin-only Category facet (multi-select): options + controlled value. */
  categories?: { value: string; label: string }[];
  category?: string[];
  onCategory?: (next: string[]) => void;
}

const GENDER_OPTIONS: { id: PrizeGender; label: string }[] = [
  { id: 'men', label: 'Men' },
  { id: 'women', label: 'Women' },
  { id: 'unisex', label: 'Unisex' },
];

/** Fixed palette always offered (Figma panel) — data colors merge in. */
const BASE_PALETTE = ['purple', 'green', 'navy', 'black', 'orange', 'red', 'blue', 'white'];

/** Known color names → swatch hex (matches the details page swatches). */
const SWATCHES: Record<string, string> = {
  black: '#1f1f1f',
  white: '#f5f5f5',
  grey: '#9695a2',
  gray: '#9695a2',
  red: '#dc2626',
  orange: '#ea580c',
  yellow: '#facc15',
  green: '#16a34a',
  blue: '#2563eb',
  navy: '#1e3a8a',
  purple: 'rgb(var(--color-primary))',
  pink: '#ec4899',
  brown: '#92400e',
};

export function PrizeFiltersButton({
  gender,
  onGender,
  color,
  onColor,
  colors,
  scoreRange,
  onScoreRange,
  maxScore,
  categories,
  category = [],
  onCategory,
}: PrizeFiltersButtonProps) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; right: number } | null>(null);

  const openPanel = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) setPanelPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    setOpen(true);
  };

  const [min, max] = scoreRange ?? [0, maxScore];

  React.useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const swatchColors = Array.from(new Set([...BASE_PALETTE, ...colors]));
  const toggle = <T,>(arr: T[], v: T): T[] =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
  // Every selected value counts — 2 genders + 3 colors + a range = (6).
  const activeCount =
    gender.length + color.length + (scoreRange ? 1 : 0) + category.length;
  const hasFilters = activeCount > 0;

  const setMin = (v: number) => onScoreRange([Math.min(Math.max(0, v), max), max]);
  const setMax = (v: number) => onScoreRange([min, Math.max(Math.min(maxScore, v), min)]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-expanded={open}
        className="inline-flex items-center gap-2 h-9 px-3 rounded-full text-sm font-bold text-text-primary/70 hover:bg-text-primary/[0.04] focus-ring active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong"
      >
        <FunnelIcon className="w-[18px] h-[18px]" />
        Filters{activeCount > 0 ? ` (${activeCount})` : ''}
      </button>
      {createPortal(
        <AnimatePresence>
          {open && panelPos && (
            <motion.div
              ref={panelRef}
              initial={{ opacity: 0, y: -4, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.97 }}
              transition={{ duration: 0.16, ease: EASE_OUT }}
              style={{ position: 'fixed', top: panelPos.top, right: panelPos.right, zIndex: 60 }}
              className="w-[280px] max-h-[min(70vh,560px)] overflow-y-auto rounded-xl border border-border-primary/40 bg-surface-elevated shadow-lg p-4 origin-top-right space-y-4"
            >
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-text-primary">Filters</p>
              <button
                type="button"
                onClick={() => {
                  onGender([]);
                  onColor([]);
                  onScoreRange(null);
                  onCategory?.([]);
                }}
                disabled={!hasFilters}
                aria-label="Reset filters"
                className="text-xs font-semibold text-text-secondary hover:text-primary-500 disabled:opacity-40 disabled:cursor-not-allowed focus-ring rounded px-1 transition-colors duration-fast"
              >
                Reset
              </button>
            </div>

            {categories && onCategory && (
              <fieldset className="space-y-2">
                <legend className="text-xs font-bold text-text-secondary uppercase tracking-wide">
                  Category
                </legend>
                <div className="space-y-1.5">
                  {categories.map((opt) => (
                    <label
                      key={opt.value}
                      className="flex items-center gap-2 text-sm text-text-primary cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={category.includes(opt.value)}
                        onChange={() => onCategory(toggle(category, opt.value))}
                        className="w-4 h-4 rounded accent-primary-500 focus-ring"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            <fieldset className="space-y-2">
              <legend className="text-xs font-bold text-text-secondary uppercase tracking-wide">
                Gender
              </legend>
              <div className="space-y-1.5">
                {GENDER_OPTIONS.map((opt) => (
                  <label
                    key={opt.id}
                    className="flex items-center gap-2 text-sm text-text-primary cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={gender.includes(opt.id)}
                      onChange={() => onGender(toggle(gender, opt.id))}
                      className="w-4 h-4 rounded accent-primary-500 focus-ring"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="space-y-2">
              <p className="text-xs font-bold text-text-secondary uppercase tracking-wide">Color</p>
              <div className="flex flex-wrap gap-2">
                {swatchColors.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => onColor(toggle(color, c))}
                    aria-label={`Color ${c}`}
                    aria-pressed={color.includes(c)}
                    className={`w-7 h-7 rounded-full border-2 focus-ring transition-transform duration-fast active:scale-90 ${
                      color.includes(c) ? 'border-primary-500 scale-110' : 'border-border-primary/40'
                    }`}
                    style={{ backgroundColor: SWATCHES[c] ?? c }}
                  >
                    {color.includes(c) && (
                      <CheckIcon className="w-4 h-4 mx-auto text-white drop-shadow" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-bold text-text-secondary uppercase tracking-wide">
                Points necessary
              </p>
              <div className="flex items-center gap-2">
                <StarIcon className="w-4 h-4 text-warning-500 shrink-0" aria-hidden="true" />
                <label className="flex-1">
                  <span className="sr-only">Minimum points</span>
                  <input
                    type="number"
                    min={0}
                    max={max}
                    value={min}
                    onChange={(e) => setMin(Number(e.target.value) || 0)}
                    placeholder="MIN"
                    className="w-full h-8 px-2 rounded-md border border-border-primary bg-transparent text-xs text-text-primary focus-ring"
                  />
                </label>
                <span className="text-xs text-text-tertiary">–</span>
                <label className="flex-1">
                  <span className="sr-only">Maximum points</span>
                  <input
                    type="number"
                    min={min}
                    max={maxScore}
                    value={max}
                    onChange={(e) => setMax(Number(e.target.value) || 0)}
                    placeholder="MAX"
                    className="w-full h-8 px-2 rounded-md border border-border-primary bg-transparent text-xs text-text-primary focus-ring"
                  />
                </label>
                <StarIcon className="w-4 h-4 text-warning-500 shrink-0" aria-hidden="true" />
              </div>
              <input
                type="range"
                min={0}
                max={maxScore}
                value={max}
                onChange={(e) => setMax(Number(e.target.value))}
                aria-label="Maximum points slider"
                className="w-full accent-primary-500"
              />
              <div className="flex justify-between text-[10px] text-text-tertiary tabular-nums">
                <span>0</span>
                <span>{maxScore}</span>
              </div>
            </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
